import { motion } from 'motion/react';
import type { CharacterProp } from '@/types/props';

interface Prop3DProps {
  prop: CharacterProp;
  size: number;
  isEquipped?: boolean;
  showEffect?: boolean;
}

const Prop3D: React.FC<Prop3DProps> = ({ prop, size, isEquipped = false }) => {
  const { slot, model } = prop;
  
  // Base styles for different prop types
  const getPropStyle = () => {
    const baseSize = size * 0.3; // Props are 30% of character size
    
    switch (slot) {
      case 'head':
        return {
          width: baseSize,
          height: baseSize * 0.8,
          top: -baseSize * 0.6,
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'hand':
        return {
          width: baseSize * 0.4,
          height: baseSize * 1.2,
          top: '40%',
          right: -baseSize * 0.3,
          transform: 'translateY(-50%)',
        };
      case 'back':
        return {
          width: baseSize * 1.2,
          height: baseSize * 1.4,
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%) translateZ(-10px)',
          zIndex: -1,
        };
      case 'accessory':
        return {
          width: baseSize * 0.6,
          height: baseSize * 0.6,
          bottom: -baseSize * 0.2,
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'face':
        return {
          width: baseSize * 0.8,
          height: baseSize * 0.4,
          top: '35%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
        };
      case 'hair':
        return {
          width: baseSize * 1.2,
          height: baseSize * 0.8,
          top: -baseSize * 0.4,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1,
        };
      default:
        return {};
    }
  };

  // Render different prop shapes
  const renderPropShape = () => {
    const depth = size * 0.05;
    
    switch (prop.id) {
      case 'wizard-hat':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Cone shape */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '80%',
                background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                boxShadow: `0 ${depth}px 0 rgba(0,0,0,0.3)`,
              }}
            />
            {/* Star decoration */}
            <div
              style={{
                position: 'absolute',
                top: '-10%',
                left: '40%',
                width: '20%',
                height: '20%',
                background: model.secondaryColor,
                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
              }}
            />
          </div>
        );
        
      case 'speed-sneakers':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Shoe shape */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                borderRadius: '40% 20% 50% 10%',
                boxShadow: `${depth}px ${depth}px 0 rgba(0,0,0,0.3)`,
              }}
            />
            {/* Lightning bolt */}
            <div
              style={{
                position: 'absolute',
                top: '20%',
                left: '30%',
                width: '40%',
                height: '60%',
                background: model.secondaryColor,
                clipPath: 'polygon(0% 0%, 60% 0%, 40% 40%, 100% 40%, 0% 100%, 40% 60%, 0% 60%)',
              }}
            />
          </div>
        );
        
      case 'shield-generator':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Shield shape */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                boxShadow: `0 ${depth}px 0 rgba(0,0,0,0.3)`,
              }}
            />
            {/* Energy core */}
            <div
              style={{
                position: 'absolute',
                top: '35%',
                left: '35%',
                width: '30%',
                height: '30%',
                background: model.glowColor,
                borderRadius: '50%',
                boxShadow: `0 0 20px ${model.glowColor}`,
              }}
            />
          </div>
        );
        
      case 'magic-wand':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Wand shaft */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: '40%',
                width: '20%',
                height: '70%',
                background: `linear-gradient(180deg, ${model.secondaryColor} 0%, ${model.color} 100%)`,
                borderRadius: '10px',
                boxShadow: `${depth}px 0 0 rgba(0,0,0,0.3)`,
              }}
            />
            {/* Star tip */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '25%',
                width: '50%',
                height: '35%',
                background: model.glowColor,
                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                boxShadow: `0 0 15px ${model.glowColor}`,
              }}
            />
          </div>
        );
        
      case 'jetpack':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Main body */}
            <div
              style={{
                position: 'absolute',
                left: '20%',
                width: '60%',
                height: '70%',
                background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                borderRadius: '20% 20% 40% 40%',
                boxShadow: `0 ${depth}px 0 rgba(0,0,0,0.3)`,
              }}
            />
            {/* Thrusters */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: '10%',
                width: '35%',
                height: '30%',
                background: model.secondaryColor,
                borderRadius: '0 0 50% 50%',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                right: '10%',
                width: '35%',
                height: '30%',
                background: model.secondaryColor,
                borderRadius: '0 0 50% 50%',
              }}
            />
          </div>
        );
        
      case 'dragon-wings':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Left wing */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: '10%',
                width: '45%',
                height: '80%',
                background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                clipPath: 'polygon(100% 0%, 100% 20%, 80% 40%, 60% 60%, 40% 80%, 20% 100%, 0% 80%, 20% 60%, 40% 40%, 60% 20%, 80% 0%)',
                transform: 'perspective(100px) rotateY(-20deg)',
                boxShadow: `-${depth}px 0 0 rgba(0,0,0,0.3)`,
              }}
            />
            {/* Right wing */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '10%',
                width: '45%',
                height: '80%',
                background: `linear-gradient(135deg, ${model.secondaryColor} 0%, ${model.color} 100%)`,
                clipPath: 'polygon(0% 0%, 20% 0%, 40% 20%, 60% 40%, 80% 60%, 100% 80%, 80% 100%, 60% 80%, 40% 60%, 20% 40%, 0% 20%)',
                transform: 'perspective(100px) rotateY(20deg)',
                boxShadow: `${depth}px 0 0 rgba(0,0,0,0.3)`,
              }}
            />
          </div>
        );
        
      case 'cool-glasses':
      case 'hipster-glasses':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Frame */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10%',
              }}
            >
              {/* Left lens */}
              <div
                style={{
                  width: '40%',
                  height: '80%',
                  background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                  borderRadius: prop.id === 'hipster-glasses' ? '20%' : '50%',
                  border: `${depth}px solid ${model.color}`,
                  boxShadow: `inset 0 0 10px rgba(0,0,0,0.3)`,
                }}
              />
              {/* Right lens */}
              <div
                style={{
                  width: '40%',
                  height: '80%',
                  background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                  borderRadius: prop.id === 'hipster-glasses' ? '20%' : '50%',
                  border: `${depth}px solid ${model.color}`,
                  boxShadow: `inset 0 0 10px rgba(0,0,0,0.3)`,
                }}
              />
              {/* Bridge */}
              <div
                style={{
                  position: 'absolute',
                  top: '45%',
                  left: '40%',
                  width: '20%',
                  height: '10%',
                  background: model.color,
                }}
              />
            </div>
          </div>
        );
        
      case 'afro-hair':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Afro shape - circular with texture */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `radial-gradient(circle at 30% 30%, ${model.secondaryColor} 0%, ${model.color} 50%, ${model.glowColor} 100%)`,
                borderRadius: '50%',
                boxShadow: `0 ${depth}px 0 rgba(0,0,0,0.3)`,
                overflow: 'hidden',
              }}
            >
              {/* Texture dots */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: '20%',
                    height: '20%',
                    background: model.glowColor,
                    borderRadius: '50%',
                    left: `${15 + (i % 3) * 30}%`,
                    top: `${15 + Math.floor(i / 3) * 30}%`,
                    opacity: 0.3,
                  }}
                />
              ))}
            </div>
          </div>
        );
        
      case 'spiky-hair':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Spikes */}
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: '20%',
                  height: '80%',
                  background: `linear-gradient(180deg, ${model.secondaryColor} 0%, ${model.color} 100%)`,
                  clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                  left: `${i * 20}%`,
                  bottom: 0,
                  transform: `rotate(${(i - 2) * 15}deg)`,
                  transformOrigin: 'bottom center',
                  boxShadow: `${depth / 2}px ${depth}px 0 rgba(0,0,0,0.3)`,
                }}
              />
            ))}
          </div>
        );
        
      case 'fancy-moustache':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Moustache shape */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, ${model.color} 0%, ${model.secondaryColor} 50%, ${model.color} 100%)`,
                clipPath: 'path("M 10,50 Q 30,20 50,50 Q 70,20 90,50 Q 80,70 50,60 Q 20,70 10,50")',
                boxShadow: `0 ${depth}px 0 rgba(0,0,0,0.3)`,
              }}
            />
          </div>
        );
        
      case 'pirate-beard':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Beard shape */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '120%',
                background: `linear-gradient(180deg, ${model.color} 0%, ${model.secondaryColor} 60%, ${model.glowColor} 100%)`,
                clipPath: 'polygon(20% 0%, 80% 0%, 90% 30%, 85% 60%, 70% 90%, 50% 100%, 30% 90%, 15% 60%, 10% 30%)',
                boxShadow: `0 ${depth}px 0 rgba(0,0,0,0.3)`,
              }}
            />
          </div>
        );
        
      case 'gamer-headphones':
        return (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Headband */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '10%',
                width: '80%',
                height: '30%',
                background: model.color,
                borderRadius: '50% 50% 0 0',
                border: `${depth}px solid ${model.secondaryColor}`,
              }}
            />
            {/* Left ear cup */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: '35%',
                height: '60%',
                background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor} 100%)`,
                borderRadius: '40%',
                boxShadow: `${depth}px ${depth}px 0 rgba(0,0,0,0.3)`,
                border: `2px solid ${model.glowColor}`,
              }}
            />
            {/* Right ear cup */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '35%',
                height: '60%',
                background: `linear-gradient(135deg, ${model.secondaryColor} 0%, ${model.color} 100%)`,
                borderRadius: '40%',
                boxShadow: `${depth}px ${depth}px 0 rgba(0,0,0,0.3)`,
                border: `2px solid ${model.glowColor}`,
              }}
            />
          </div>
        );
        
      default:
        // Generic prop shape
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${model.color} 0%, ${model.secondaryColor || model.color} 100%)`,
              borderRadius: '20%',
              boxShadow: `${depth}px ${depth}px 0 rgba(0,0,0,0.3)`,
            }}
          />
        );
    }
  };

  return (
    <motion.div
      style={{
        position: 'absolute',
        ...getPropStyle(),
      }}
      animate={{
        scale: isEquipped ? [1, 1.1, 1] : 1,
        rotate: slot === 'accessory' ? [0, 5, -5, 0] : 0,
      }}
      transition={{
        scale: {
          duration: 2,
          repeat: isEquipped ? Infinity : 0,
          ease: 'easeInOut',
        },
        rotate: {
          duration: 3,
          repeat: isEquipped ? Infinity : 0,
          ease: 'easeInOut',
        },
      }}
    >
      {renderPropShape()}
      
      {/* Glow effect when equipped */}
      {isEquipped && model.glowColor && (
        <motion.div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '120%',
            height: '120%',
            transform: 'translate(-50%, -50%)',
            background: `radial-gradient(circle, ${model.glowColor}40 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
          animate={{
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}
    </motion.div>
  );
};

export default Prop3D;